// File path: /core/frontend/src/config/moduleRegistry.ts
// Module Registry - Defines all LumiLearn modules and their configuration

export type ModuleId = 'core' | 'elevate' | 'navigate' | 'bridge' | 'pulse' | 'pathway' | 'convergence' | 'harmony';

export type ModuleTier = 'basic' | 'professional' | 'enterprise';

export interface ModuleFeature {
  id: string;
  name: string;
  description: string;
  tier: ModuleTier; // Minimum tier required
}

export interface ModuleDefinition {
  id: ModuleId;
  name: string;
  description: string;
  icon: string; // Lucide icon name
  color: string; // Tailwind color or hex
  logo?: string; // Full logo image path (for expanded sidebar)
  logoIcon?: string; // Icon-only logo path (for collapsed sidebar)
  basePath: string; // Route base path
  apiPath: string; // API endpoint prefix
  isCore: boolean; // Core modules are always available
  displayOrder: number;
  features: ModuleFeature[];
  dependencies: ModuleId[]; // Other modules this one depends on
}

// Module definitions
export const MODULE_REGISTRY: Record<ModuleId, ModuleDefinition> = {
  core: {
    id: 'core',
    name: 'Core',
    description: 'Core platform services - authentication, users, organizations',
    icon: 'Settings',
    color: '#6366f1', // Indigo
    basePath: '/',
    apiPath: '/api',
    isCore: true,
    displayOrder: 0,
    features: [
      { id: 'auth', name: 'Authentication', description: 'Login, SSO, MFA', tier: 'basic' },
      { id: 'users', name: 'User Management', description: 'User CRUD and roles', tier: 'basic' },
      { id: 'orgs', name: 'Organizations', description: 'District/school hierarchy', tier: 'basic' },
      { id: 'notifications', name: 'Notifications', description: 'Email and in-app alerts', tier: 'basic' },
    ],
    dependencies: [],
  },

  elevate: {
    id: 'elevate',
    name: 'Elevate',
    description: 'Teacher evaluation and professional development',
    icon: 'TrendingUp',
    color: '#2CB1BC', // Teal - matches brand color
    logo: '/logos/elevate-logo.png',
    logoIcon: '/logos/elevate-icon.png',
    basePath: '/elevate',
    apiPath: '/api/elevate',
    isCore: false,
    displayOrder: 1,
    features: [
      { id: 'evaluations', name: 'Evaluations', description: 'Formal teacher observations', tier: 'basic' },
      { id: 'walkthroughs', name: 'Walkthroughs', description: 'Informal classroom visits', tier: 'basic' },
      { id: 'lesson-plans', name: 'Lesson Plan Review', description: 'Rubric-based lesson scoring', tier: 'professional' },
      { id: 'resources', name: 'Resource Library', description: 'PD resources with semantic search', tier: 'basic' },
      { id: 'assessments', name: 'Assessment Data', description: 'Student assessment integration', tier: 'professional' },
      { id: 'analytics', name: 'Advanced Analytics', description: 'Trend analysis and benchmarks', tier: 'enterprise' },
    ],
    dependencies: ['core'],
  },

  navigate: {
    id: 'navigate',
    name: 'Navigate',
    description: 'MTSS intervention tracking and early warning system',
    icon: 'MapPin', // Compass/navigation concept
    color: '#E48D14', // Amber - brand color from style guide
    logo: '/logos/navigate-logo.png',
    logoIcon: '/logos/navigate-icon.png',
    basePath: '/navigate',
    apiPath: '/api/navigate',
    isCore: false,
    displayOrder: 2,
    features: [
      { id: 'students', name: 'Student Profiles', description: 'Whole-child student view', tier: 'basic' },
      { id: 'interventions', name: 'Interventions', description: 'Tiered intervention tracking', tier: 'basic' },
      { id: 'touchpoints', name: 'TouchPoints', description: 'Student interaction logging', tier: 'basic' },
      { id: 'attendance', name: 'Attendance', description: 'Attendance tracking and alerts', tier: 'basic' },
      { id: 'behavior', name: 'Behavior', description: 'Behavior incident tracking', tier: 'professional' },
      { id: 'ews', name: 'Early Warning System', description: 'Risk prediction algorithms', tier: 'enterprise' },
    ],
    dependencies: ['core'],
  },

  bridge: {
    id: 'bridge',
    name: 'Bridge',
    description: 'Special education compliance and IEP management',
    icon: 'Bridge',
    color: '#8b5cf6', // Violet
    logo: '/logos/bridge-logo.png',
    logoIcon: '/logos/bridge-icon.png',
    basePath: '/bridge',
    apiPath: '/api/bridge',
    isCore: false,
    displayOrder: 3,
    features: [
      { id: 'ieps', name: 'IEP Management', description: 'IEP document creation and tracking', tier: 'basic' },
      { id: 'compliance', name: 'Compliance', description: 'Timeline and deadline tracking', tier: 'basic' },
      { id: 'goals', name: 'Goal Tracking', description: 'IEP goal progress monitoring', tier: 'basic' },
      { id: 'accommodations', name: 'Accommodations', description: 'Accommodation management', tier: 'professional' },
      { id: 'reporting', name: 'State Reporting', description: 'Automated state compliance reports', tier: 'enterprise' },
    ],
    dependencies: ['core'],
  },

  pulse: {
    id: 'pulse',
    name: 'Pulse',
    description: 'Student performance dashboard — aggregates assessment outcomes across modules',
    icon: 'Activity',
    color: '#ec4899', // Pink
    logo: '/logos/pulse-logo.png',
    logoIcon: '/logos/pulse-icon.png',
    basePath: '/pulse',
    apiPath: '/api/pulse',
    isCore: false,
    displayOrder: 4,
    features: [
      { id: 'dashboards', name: 'Dashboards', description: 'Configurable analytics dashboards', tier: 'basic' },
      { id: 'reports', name: 'Reports', description: 'Standard and custom reports', tier: 'basic' },
      { id: 'data-integration', name: 'Data Integration', description: 'Cross-module data aggregation', tier: 'professional' },
      { id: 'predictive', name: 'Predictive Analytics', description: 'ML-powered predictions', tier: 'enterprise' },
    ],
    dependencies: ['core'],
  },

  // Planned: AI-powered personalized learning pathways. NOTE: the "Pathway" name was
  // previously used for the analytics dashboard — that module is now "Pulse" (above).
  // This is a reserved slot for the future AI curriculum tool.
  pathway: {
    id: 'pathway',
    name: 'Pathway',
    description: 'AI-powered personalized learning pathways (planned)',
    icon: 'Route',
    color: '#23ABA7', // Teal-indigo
    logo: '/logos/pathway-logo.png',
    logoIcon: '/logos/pathway-icon.png',
    basePath: '/pathway',
    apiPath: '/api/pathway',
    isCore: false,
    displayOrder: 5,
    features: [],
    dependencies: ['core'],
  },

  // Placeholder (CLAUDE.md: "TBD"). No product description or features until it is defined (D-D).
  convergence: {
    id: 'convergence',
    name: 'Convergence',
    description: 'Placeholder — not yet defined',
    icon: 'GitMerge',
    color: '#06b6d4', // Cyan
    logo: '/logos/convergence-logo.png',
    logoIcon: '/logos/convergence-icon.png',
    basePath: '/convergence',
    apiPath: '/api/convergence',
    isCore: false,
    displayOrder: 6,
    features: [],
    dependencies: ['core'],
  },

  // Placeholder (CLAUDE.md: "TBD"). No product description or features until it is defined (D-D).
  harmony: {
    id: 'harmony',
    name: 'Harmony',
    description: 'Placeholder — not yet defined',
    icon: 'Calendar',
    color: '#84cc16', // Lime
    logo: '/logos/harmony-logo.png',
    logoIcon: '/logos/harmony-icon.png',
    basePath: '/harmony',
    apiPath: '/api/harmony',
    isCore: false,
    displayOrder: 7,
    features: [],
    dependencies: ['core'],
  },
};

// Helper functions

/**
 * Get all modules sorted by display order
 */
export function getAllModules(): ModuleDefinition[] {
  return Object.values(MODULE_REGISTRY).sort((a, b) => a.displayOrder - b.displayOrder);
}

/**
 * Get only subscribable modules (excludes core)
 */
export function getSubscribableModules(): ModuleDefinition[] {
  return getAllModules().filter(m => !m.isCore);
}

/**
 * Get a module by ID
 */
export function getModule(id: ModuleId): ModuleDefinition | undefined {
  return MODULE_REGISTRY[id];
}

/**
 * Get module by API path (for routing)
 */
export function getModuleByApiPath(path: string): ModuleDefinition | undefined {
  return Object.values(MODULE_REGISTRY).find(m => path.startsWith(m.apiPath));
}

/**
 * Get module by route path (for navigation)
 */
export function getModuleByRoutePath(path: string): ModuleDefinition | undefined {
  return Object.values(MODULE_REGISTRY).find(m =>
    m.basePath !== '/' && path.startsWith(m.basePath)
  );
}

/**
 * Check if a feature requires a specific tier
 */
export function getFeatureTier(moduleId: ModuleId, featureId: string): ModuleTier | undefined {
  const module = MODULE_REGISTRY[moduleId];
  if (!module) return undefined;
  const feature = module.features.find(f => f.id === featureId);
  return feature?.tier;
}

/**
 * Check if a tier includes another tier
 */
export function tierIncludes(userTier: ModuleTier, requiredTier: ModuleTier): boolean {
  const tierOrder: ModuleTier[] = ['basic', 'professional', 'enterprise'];
  return tierOrder.indexOf(userTier) >= tierOrder.indexOf(requiredTier);
}

// Type for subscription data returned from API
export interface ModuleSubscription {
  moduleId: ModuleId;
  moduleName: string;
  tier: ModuleTier;
  enabledFeatures: string[];
}

export interface OrganizationSubscription {
  id: string;
  organizationId: string;
  licenseType: 'saas' | 'dedicated' | 'enterprise';
  status: 'active' | 'trial' | 'suspended' | 'expired';
  startDate: string;
  endDate: string;
  modules: ModuleSubscription[];
}
